import { useData } from 'vike-react/useData';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Layers,
  TrendingDown,
} from 'lucide-react';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../components/motion/StaggerGrid';
import { CountUp } from '../../components/motion/CountUp';
import {
  ParticulierAidesCrossLink,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKeyPointsGrid,
  ParticulierKPIBand,
  ParticulierLiveCard,
  ParticulierPageHero,
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  type ParticulierKpi,
} from '../../components/audience/particulier';
import { TRAVAUX_PARTICULIERS } from '../../config/travaux-particuliers';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import type { Data } from './+data';

const AUDIENCE_ID = 'particuliers' as const;

// ════════════════════════════════════════════════════════════════
// CONFIG : KPI Band sous le hero — 3 chiffres clés
// ════════════════════════════════════════════════════════════════

const KPI_PARTICULIERS: ParticulierKpi[] = [
  {
    value: "Jusqu'à 90 %",
    numericValue: 90,
    suffix: ' %',
    label: 'du coût des travaux pris en charge',
    source: 'Pour les ménages aux revenus modestes (cumul aides 2026)',
  },
  {
    value: '5 aides',
    numericValue: 5,
    suffix: ' aides',
    label: 'cumulables sur un même chantier',
    source: "MaPrimeRénov' + prime énergie (CEE) + éco-prêt à taux zéro + TVA réduite à 5,5 % + Coup de pouce",
  },
  {
    value: '60 sec',
    numericValue: 60,
    suffix: ' sec',
    label: "pour estimer votre cumul d'aides",
    source: 'Calculateur gratuit · sans inscription · sources officielles',
  },
];

// ════════════════════════════════════════════════════════════════
// CONFIG : Top 6 travaux populaires
// ════════════════════════════════════════════════════════════════

const POPULAR_TRAVAUX_SLUGS = [
  'pompe-a-chaleur',
  'isolation-combles',
  'isolation-murs',
  'poele-a-granules',
  'chauffe-eau-thermodynamique',
  'vmc-double-flux',
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  isolation: 'Isolation',
  chauffage: 'Chauffage',
  ecs: 'Eau chaude',
  ventilation: 'Ventilation',
};

const CATEGORY_TONES: Record<string, string> = {
  isolation: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  chauffage: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  ecs: 'bg-sky-50 text-sky-700 ring-sky-200/60',
  ventilation: 'bg-violet-50 text-violet-700 ring-violet-200/60',
};

// ════════════════════════════════════════════════════════════════
// CONFIG : 4 promesses sur la section "Pourquoi c'est plus simple"
// ════════════════════════════════════════════════════════════════

const PARTICULIERS_KEY_POINTS = [
  {
    iconName: 'wallet',
    label: 'Toutes vos aides étudiées en une fois',
    description:
      "MaPrimeRénov\u2019, prime énergie (CEE), éco-PTZ, TVA à 5,5 % et aides locales. Pas de calcul Excel à faire vous-même.",
  },
  {
    iconName: 'handshake',
    label: "Un artisan RGE (Reconnu Garant de l'Environnement) de notre réseau",
    description:
      "On vous présente un pro vérifié près de chez vous : certification RGE (Reconnu Garant de l'Environnement) active, assurance décennale à jour, retours clients consultables.",
  },
  {
    iconName: 'shield-check',
    label: "Pas d\u2019avance, pas de mauvaise surprise",
    description:
      "Quand vous passez par notre réseau, le devis intègre les aides en déduction directe. Vous payez le reste, c\u2019est tout.",
  },
  {
    iconName: 'sparkles',
    label: 'Un suivi clair de A à Z',
    description:
      "On vous tient informé à chaque étape, du devis jusqu\u2019au versement de la prime par l\u2019organisme officiel.",
  },
] as const;

// ════════════════════════════════════════════════════════════════
// PAGE
// ════════════════════════════════════════════════════════════════

export default function ParticuliersPage() {
  const { journey, tools, cases } = useData<Data>();
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  const miniActions = [
    {
      label: 'Pompe à chaleur',
      href: '/particuliers/travaux/pompe-a-chaleur',
      iconName: 'snowflake',
    },
    {
      label: 'Isolation combles',
      href: '/particuliers/travaux/isolation-combles',
      iconName: 'home',
    },
    { label: 'Tous les travaux', href: '/particuliers/travaux', iconName: 'layers' },
  ];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Particuliers', url: 'https://solutionecofrance.fr/particuliers' },
        ]}
      />

      {/* ─── 1. HERO (kit Particulier) ───────────────────────── */}
      <ParticulierPageHero
        eyebrow={voice.hero.eyebrow}
        eyebrowIconName="home"
        headline={voice.hero.headline}
        headlineAccent={voice.hero.headlineAccent}
        subheadline={voice.hero.subheadline}
        primaryCta={voice.hero.primaryCta}
        secondaryCta={voice.hero.secondaryCta}
        bullets={voice.hero.bullets}
        microPromise={voice.hero.microPromise}
        miniActions={miniActions}
        sideSlot={<AidesPreviewCard />}
      />

      {/* ─── 2. KPI BAND animé ──────────────────────────────── */}
      <ParticulierKPIBand items={KPI_PARTICULIERS} />

      {/* ─── 3. POURQUOI C'EST PLUS SIMPLE ───────────────────── */}
      <ParticulierKeyPointsGrid
        eyebrow={voice.proof.eyebrow}
        title="On vous fait gagner du temps, et de l'argent."
        description="Concrètement, voici ce qu'on prend en charge dès le premier rendez-vous."
        points={PARTICULIERS_KEY_POINTS.map((p) => ({ ...p }))}
        cols={2}
        align="center"
      />

      {/* ─── 4. TRAVAUX POPULAIRES (custom) ──────────────────── */}
      <PopularTravauxSection />

      {/* ─── 5. JOURNEY TIMELINE ────────────────────────────── */}
      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {/* ─── 6. AIDES CUMULABLES (custom) ────────────────────── */}
      <CumulableAidesSection />

      {/* ─── 7. TOOLS ───────────────────────────────────────── */}
      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      {/* ─── 8. CAS CONCRET ─────────────────────────────────── */}
      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      {/* ─── 9. FAQ (kit Particulier) ───────────────────────── */}
      <ParticulierFAQ
        eyebrow={voice.faq.eyebrow}
        title={voice.faq.title}
        items={voice.faq.items.map((item) => ({
          question: item.q,
          answer: item.a,
        }))}
      />

      {/* ─── 10. CTA FINAL (kit Particulier) ──────────────────── */}
      <ParticulierCTAFinal
        badge={voice.ctaFinal.badge}
        title={voice.ctaFinal.title}
        description={voice.ctaFinal.description}
        primaryCta={voice.ctaFinal.primary}
        secondaryCta={voice.ctaFinal.secondary}
        bullets={['Aucune carte bancaire', 'Sources officielles', 'Aucune relance commerciale']}
        footnote={voice.ctaFinal.footnote}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// SIDE CARD HERO : aperçu visuel des 4 aides 2026
// ════════════════════════════════════════════════════════════════

function AidesPreviewCard() {
  const aides = [
    { label: "MaPrimeRénov\u2019", value: 'selon barème 2026', tone: 'brand' as const },
    { label: 'Prime énergie (CEE)', value: "cumulable avec MaPrimeRénov'", tone: 'accent' as const },
    { label: 'Éco-PTZ 0 % (15 ans)', value: 'sans condition de ressources', tone: 'lavender' as const },
    { label: 'TVA 5,5 % (vs 20 %)', value: 'appliquée par l\u2019artisan', tone: 'sage' as const },
  ];

  const toneRing: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-800 ring-brand-200/70',
    accent: 'bg-accent-50 text-accent-800 ring-accent-200/70',
    lavender: 'bg-lavender-50 text-lavender-800 ring-lavender-200/70',
    sage: 'bg-stone-50 text-stone-700 ring-stone-200/70',
  };

  return (
    <ParticulierLiveCard
      eyebrow="Vos aides 2026"
      subEyebrow="Cumul possible · scénario type maison"
      badge={
        <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
          <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
          2026
        </span>
      }
      footer="Cumul possible : MaPrimeRénov' + prime énergie (CEE) + éco-prêt à taux zéro + TVA réduite"
    >
      <div className="space-y-2.5">
        {aides.map((aide) => (
          <div
            key={aide.label}
            className={cn(
              'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 ring-1 ring-inset',
              toneRing[aide.tone],
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold leading-tight">{aide.label}</p>
              <p className="mt-0.5 text-[11px] opacity-70">{aide.value}</p>
            </div>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2.4} />
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl bg-gradient-to-br from-ink to-stone-900 p-3 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory-50/60">
          Reste à charge minimum
        </p>
        <p className="mt-1 font-display text-2xl font-bold text-ivory-50 tabular-nums">
          dès <CountUp end={10} suffix=" %" />
        </p>
        <p className="mt-0.5 text-[10.5px] text-ivory-50/60">
          Pour ménages très modestes (barème MaPrimeRénov')
        </p>
      </div>
    </ParticulierLiveCard>
  );
}

// ════════════════════════════════════════════════════════════════
// SECTION : Top 6 travaux populaires (inchangée — déjà en kit)
// ════════════════════════════════════════════════════════════════

function PopularTravauxSection() {
  const travaux = POPULAR_TRAVAUX_SLUGS
    .map((slug) => TRAVAUX_PARTICULIERS.find((t) => t.slug === slug))
    .filter((t): t is NonNullable<typeof t> => Boolean(t));

  return (
    <section className={cn(PARTICULIER_SECTION.alternateBg, PARTICULIER_SECTION.standard)}>
      <Container>
        <Reveal>
          <div className="mx-auto mb-10 max-w-2xl text-center md:mb-14">
            <p className="eyebrow justify-center">Travaux les plus financés</p>
            <h2 className="mt-3 h-section">
              Les 6 chantiers où vos aides{' '}
              <span className={PARTICULIER_GRADIENT.headline}>vont le plus loin.</span>
            </h2>
            <p className="mt-3 text-[15px] text-stone-600 leading-relaxed">
              Pour chaque opération, on calcule le cumul possible MaPrimeRénov' + Prime énergie (CEE) + Coup de pouce.
              Les montants ci-dessous sont indicatifs (cumul maximum 2026).
            </p>
          </div>
        </Reveal>

        <StaggerGrid className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {travaux.map((travail, idx) => {
            const Icon = getIcon(travail.iconName);
            const categoryLabel = CATEGORY_LABELS[travail.category];
            const categoryTone = CATEGORY_TONES[travail.category] ?? CATEGORY_TONES.isolation;

            return (
              <StaggerItem key={travail.slug}>
                <motion.a
                  href={`/particuliers/travaux/${travail.slug}`}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.15, ease: easeStripe }}
                  className="group relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.12)]"
                >
                  <div
                    aria-hidden
                    className={cn(
                      'absolute inset-x-0 top-0 h-px',
                      PARTICULIER_GRADIENT.cardTopHairline,
                    )}
                  />

                  <span className="absolute right-4 top-4 font-mono text-[10px] font-bold uppercase tracking-wider text-stone-300">
                    0{idx + 1}
                  </span>

                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-200 ease-out group-hover:scale-105',
                        PARTICULIER_GRADIENT.iconHalo,
                      )}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ring-1 ring-inset',
                          categoryTone,
                        )}
                      >
                        {categoryLabel}
                      </span>
                      <p className="mt-2 font-display text-lg font-bold leading-tight text-ink">
                        {travail.title}
                      </p>
                    </div>
                  </div>

                  <p className="text-[13.5px] leading-relaxed text-stone-600">
                    {travail.effyPromise}
                  </p>

                  <div className="mt-auto grid grid-cols-2 gap-3 border-t border-stone-100 pt-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                        Économies
                      </p>
                      <p className="mt-1 inline-flex items-baseline gap-1 font-display text-xl font-bold tabular-nums text-ink">
                        <TrendingDown className="h-4 w-4 text-brand-600" strokeWidth={2} />
                        <CountUp end={travail.savingsPercent} suffix=" %" />
                      </p>
                      <p className="text-[10px] text-stone-500">{travail.savingsLabel}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                        Aides max
                      </p>
                      <p
                        className={cn(
                          'mt-1 font-display text-xl font-bold tabular-nums',
                          PARTICULIER_GRADIENT.aideAmount,
                        )}
                      >
                        <CountUp end={travail.maxAidEur} suffix=" €" />
                      </p>
                      <p className="text-[10px] text-stone-500">
                        Cumul MaPrimeRénov' + prime énergie (CEE) + Coup de pouce
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="font-mono text-[10px] text-stone-400">
                      Fiche {travail.ficheCode}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                      Voir le guide
                      <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                    </span>
                  </div>
                </motion.a>
              </StaggerItem>
            );
          })}
        </StaggerGrid>

        <Reveal delay={0.1}>
          <div className="mt-10 flex justify-center">
            <a
              href="/particuliers/travaux"
              className="group inline-flex items-center gap-2 rounded-full border border-stone-200 bg-paper px-5 py-2.5 text-[13px] font-semibold text-ink shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
            >
              Voir tous les travaux éligibles
              <ArrowUpRight
                className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.2}
              />
            </a>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// SECTION : Aides cumulables — Cross-link + Stack visualisation
// ════════════════════════════════════════════════════════════════

function CumulableAidesSection() {
  return (
    <>
      <ParticulierAidesCrossLink
        eyebrow="Les 5 aides nationales"
        title="5 dispositifs cumulables pour faire fondre votre reste à charge"
        description="MaPrimeRénov', Prime énergie (CEE), éco-PTZ, TVA à 5,5 % et Coup de pouce ont été conçus pour s'additionner sur un même chantier."
        variant="compact"
      />

      <section className={cn(PARTICULIER_SECTION.alternateBg, PARTICULIER_SECTION.compact)}>
        <Container>
          <div className="mx-auto grid max-w-5xl items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
            <Reveal>
              <div>
                <p className="eyebrow">Comment ça s'empile</p>
                <h2 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight text-balance text-ink md:text-[28px]">
                  Empilez les aides{' '}
                  <span className={PARTICULIER_GRADIENT.headline}>pour réduire votre reste à charge.</span>
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-stone-600">
                  Chaque dispositif fonctionne de manière indépendante mais cumulable.
                  L'ordre des démarches compte : engagez MaPrimeRénov' et la Prime énergie{' '}
                  <span className="font-semibold text-ink">avant</span> de signer le devis.
                </p>

                <ul className="mt-6 space-y-2.5">
                  {[
                    "Demande MaPrimeRénov' et prime énergie (CEE) engagées avant signature du devis",
                    "Artisan certifié RGE (Reconnu Garant de l'Environnement) pour les travaux concernés",
                    'Justificatifs conservés 5 ans (devis, factures, attestations)',
                    'Éco-PTZ disponible pour financer le reste à charge',
                  ].map((rule) => (
                    <li key={rule} className="flex items-start gap-2.5 text-[13.5px] text-stone-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.2} />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <a
                    href="/particuliers/calculer-mes-aides"
                    className="group inline-flex items-center gap-2 rounded-md bg-ink px-4 py-2.5 text-[13px] font-semibold text-ivory-50 shadow-[0_1px_2px_rgba(10,10,10,.08)] transition-all duration-150 ease-out hover:-translate-y-0.5 hover:bg-stone-800 hover:shadow-elevated"
                  >
                    Calculer mon cumul personnalisé
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" strokeWidth={2} />
                  </a>
                  <a
                    href="/particuliers/aides"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-800"
                  >
                    Comprendre chaque aide
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <CumulStackVisualization />
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}

function CumulStackVisualization() {
  const layers = [
    { name: "MaPrimeRénov'", sub: 'Forfait Anah selon vos revenus', amount: "Jusqu'à 5 000 €", tone: 'brand' },
    { name: 'Prime énergie (CEE)', sub: "Versée par l'obligé · sans condition", amount: '≈ 4 200 €', tone: 'accent' },
    { name: 'Coup de pouce', sub: 'Bonus prime énergie (CEE) sur pompe à chaleur, biomasse…', amount: '+ 500 à 1 000 €', tone: 'lavender' },
    { name: 'TVA à 5,5 %', sub: 'Auto · au lieu de 20 % standard', amount: '−2 400 €', tone: 'sage' },
    { name: 'Éco-PTZ', sub: 'Prêt à 0 % pour le reste à charge', amount: "Jusqu'à 50 000 €", tone: 'ink' },
  ] as const;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-200/70 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] md:p-8">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/40 via-transparent to-accent-50/30"
      />

      <div className="relative">
        <div className="mb-5 flex items-center gap-2 border-b border-stone-100 pb-4">
          <Layers className="h-4 w-4 text-brand-700" strokeWidth={1.8} />
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Empilage type · PAC air/eau · ménage modeste
          </p>
        </div>

        <StaggerGrid className="space-y-2.5">
          {layers.map((layer, idx) => {
            const toneClass =
              layer.tone === 'brand'
                ? 'border-brand-200/70 bg-gradient-to-r from-brand-50 to-brand-50/30 text-brand-800 [&_.amount]:text-brand-800'
                : layer.tone === 'accent'
                  ? 'border-accent-200/70 bg-gradient-to-r from-accent-50 to-accent-50/30 text-accent-800 [&_.amount]:text-accent-800'
                  : layer.tone === 'lavender'
                    ? 'border-lavender-200/70 bg-gradient-to-r from-lavender-50 to-lavender-50/30 text-lavender-800 [&_.amount]:text-lavender-800'
                    : layer.tone === 'sage'
                      ? 'border-stone-200 bg-gradient-to-r from-stone-50 to-stone-50/30 text-stone-700 [&_.amount]:text-stone-700'
                      : 'border-stone-300 bg-gradient-to-r from-stone-100 to-stone-50 text-ink [&_.amount]:text-ink';

            return (
              <StaggerItem key={layer.name}>
                <div
                  className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${toneClass}`}
                  style={{ marginLeft: `${idx * 6}px` }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold">{layer.name}</p>
                    <p className="mt-0.5 text-[11px] opacity-70">{layer.sub}</p>
                  </div>
                  <span className="amount font-mono text-[13px] font-bold tabular-nums">
                    {layer.amount}
                  </span>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGrid>

        <div className="mt-5 rounded-xl bg-gradient-to-br from-ink to-stone-900 p-4 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory-50/60">
            Jusqu'à
          </p>
          <p className="mt-1 font-display text-3xl font-bold tracking-tight text-ivory-50 tabular-nums">
            <CountUp end={90} suffix=" %" /> du coût pris en charge
          </p>
          <p className="mt-1 text-[11px] text-ivory-50/60">
            Pour les ménages modestes · cumul aides directes + financement éco-PTZ
          </p>
        </div>
      </div>
    </div>
  );
}
